import { useState } from 'react';
import { storeParams, getParams } from '@op/services/serverStorage';
import {
  setDrilldownLocal,
  getDrilldownLocal,
  setDownloadLocal,
  getDownloadLocal,
} from '@op/utils/localParamsStorage';

/** 参数存储类型 */
export enum EStorageType {
  /** 下钻跳转参数 */
  Drilldown = 'drilldown',
  /** 下载参数 */
  Download = 'download',
}

/** 存储来源 */
export enum EStorageSource {
  /** 服务端存储 */
  Server = 'server',
  /** 本地存储（sessionStorage / localStorage） */
  Local = 'local',
}

/** 本地存储默认过期时间（毫秒），3 小时 */
const LOCAL_EXPIRE_MS = 3 * 60 * 60 * 1000;

type TSaveResult = {
  /** 参数 id（服务端返回 MD5 或本地生成的 UUID） */
  id: string;
  /** 数据实际存储在哪里 */
  source: EStorageSource;
};

type TUseServerStorageOptions = {
  /** 存储类型（必填，由上层 Hook 传入） */
  type: EStorageType;
  /** 服务端存储失败时是否 fallback 到本地存储，默认 true */
  fallback?: boolean;
};

type TUseServerStorageReturn = {
  /** 保存参数，返回 { id, source }。服务端存储时 id 为 param_md5（MD5），本地存储时为 UUID */
  save: (data: any) => Promise<TSaveResult | null>;
  /** 读取参数，source 指定优先读取来源 */
  get: (id: string, source?: EStorageSource) => Promise<any | null>;
  /**
   * 从当前页面 URL 读取参数（一站式）
   * 自动提取 id 和 _s 标记，优先从对应来源读取
   *
   * @param idKey URL 中的 id 参数名，如 'statsDetailId' 或 'id'
   */
  getFromUrl: (idKey: string) => Promise<any | null>;
  /** 是否正在保存 */
  saving: boolean;
  /** 是否正在读取 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
};

/**
 * 通用服务端存储 Hook
 *
 * 用法：
 *   const { save, get, getFromUrl } = useServerStorage({ type: EStorageType.Drilldown });
 *
 *   // 写入
 *   const result = await save(data);
 *
 *   // 读取（目标页面，一行搞定）
 *   const params = await getFromUrl('statsDetailId');
 */
export function useServerStorage(options: TUseServerStorageOptions): TUseServerStorageReturn {
  const { type, fallback = true } = options;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readLocal = (id: string): any | null => {
    if (type === EStorageType.Drilldown) {
      return getDrilldownLocal(id);
    }
    return getDownloadLocal(id);
  };

  const save = async (data: any): Promise<TSaveResult | null> => {
    setSaving(true);
    setError(null);
    try {
      // 服务端存储：JSON.stringify → POST /frontend/cache/create → 返回 param_md5
      const paramsStr = JSON.stringify(data);
      const paramMd5 = await storeParams({ params: paramsStr });
      if (paramMd5) return { id: paramMd5, source: EStorageSource.Server };
    } catch (err) {
      console.warn(`[useServerStorage] 服务端存储失败(${type})，fallback 到本地:`, err);
      if (!fallback) {
        setError(err instanceof Error ? err.message : '存储失败');
        return null;
      }
    } finally {
      setSaving(false);
    }

    if (fallback) {
      const id = type === EStorageType.Drilldown
        ? setDrilldownLocal(data, LOCAL_EXPIRE_MS)
        : setDownloadLocal(data, LOCAL_EXPIRE_MS);
      return { id, source: EStorageSource.Local };
    }
    return null;
  };

  const get = async (id: string, source: EStorageSource = EStorageSource.Local): Promise<any | null> => {
    setLoading(true);
    setError(null);

    try {
      if (source === EStorageSource.Server) {
        // 服务端获取：POST /frontend/cache/get → JSON.parse 还原
        // 注意：http 拦截器已解包，getParams 直接 resolve 缓存原文（字符串），非 { data } 结构
        const res = await getParams({ param_md5: id });
        if (res) {
          return JSON.parse(res);
        }
      }

      if (source === EStorageSource.Local) {
        return readLocal(id);
      } 
    } catch (err) {
      console.warn(`[useServerStorage] 服务端获取失败(${type})，fallback 到本地:`, err);
      if (!fallback) {
        setError(err instanceof Error ? err.message : '获取失败');
        return null;
      }
    } finally {
      setLoading(false);
    }

    if (fallback) {
      return readLocal(id);
    }
    return null;
  };

  /**
   * 从当前页面 URL 读取参数（一站式）
   * 自动提取 id 和 _s 标记，优先从对应来源读取
   */
  const getFromUrl = async (idKey: string): Promise<any | null> => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get(idKey);
    if (!id) return null;

    const source = urlParams.get('_s') as EStorageSource | null;
    return get(id, source || undefined);
  };

  return { save, get, getFromUrl, saving, loading, error };
}
